/**
 * Button — design/09 §8: "button (primary / secondary / ghost / destructive)".
 *
 * This file is on the brand-red chrome allowlist. §5.2 rule 1 names "primary-button
 * fill" as one of the four permitted sites for brand red, and this is that site. The
 * DESTRUCTIVE variant deliberately does NOT use brand red — it uses
 * `status-critical`, because a destructive action is a semantic state and brand red
 * is an identity colour. That the two are 4.9 ΔE apart is the whole reason the
 * distinction is drawn by role rather than by hue.
 */

import { forwardRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { Loader2 } from 'lucide-react';
import { FOCUS_RING } from '../focus';

export const BUTTON_VARIANTS = ['primary', 'secondary', 'ghost', 'destructive'] as const;
export type ButtonVariant = (typeof BUTTON_VARIANTS)[number];

export const BUTTON_SIZES = ['sm', 'md', 'lg'] as const;
export type ButtonSize = (typeof BUTTON_SIZES)[number];

const VARIANT_CLASSES: Record<ButtonVariant, string> = {
  // §5.2 rule 1 — the primary-button fill is one of the four sanctioned uses of brand red.
  // ADR 9004 - hover is a COLOUR, not an opacity. The redesign's stylesheet darkens the
  // fill (`brand-red-hover`); fading it to 90% instead let the page plane show through, so
  // the same button read as a slightly different red on a raised panel than on a sunken
  // one. §5.2 rule 1 sanctions brand red at the primary-button fill, and the hover state of
  // that fill is the same site rather than a fifth one.
  // The PRESSED state used to be `active:opacity-80`, which is the defect the paragraph
  // above rejects for hover, left standing one state along. §5.1 declares no pressed step
  // for brand red and a hex invented here would be a colour nobody designed, so pressed is
  // the sanctioned hover colour plus one hairline of travel — a cue that survives a touch
  // screen, where there is no hover at all.
  //
  // DISABLED IS NEUTRAL, not the fill at half opacity. `disabled:opacity-50` over brand red
  // on the cream page reads as PINK (audit v4 V-2) — a colour §5.1 does not have, on the one
  // control whose red is sanctioned. A disabled primary is not a use of the brand colour at
  // all, so it takes the surface and ink of an inactive control and drops the opacity. This
  // removes red from a site rather than adding one, so it cannot breach §5.2's four.
  primary: 'bg-brand-red text-ink-on-brand border border-brand-red hover:bg-ramp-brand-red-hover hover:border-ramp-brand-red-hover active:bg-ramp-brand-red-hover active:border-ramp-brand-red-hover active:translate-y-hairline disabled:bg-surface-sunken disabled:border-hairline disabled:text-ink-muted disabled:opacity-100 disabled:hover:bg-surface-sunken disabled:hover:border-hairline disabled:active:translate-y-0',
  secondary: 'bg-surface-raised text-ink-primary border border-hairline hover:bg-surface-sunken',
  ghost: 'bg-transparent text-ink-primary border border-transparent hover:bg-surface-sunken',
  // NOT brand red. A destructive action is a semantic state; `status-critical` is the
  // token for that, and it lives in the semantic palette rather than the brand one.
  // `border-status-critical`, not `border-color-status-critical`: the colour group is
  // `status`. The longer spelling emitted no rule, so this button drew the default border
  // colour and the one thing marking it destructive was its label.
  destructive: 'bg-transparent text-status-critical border border-status-critical hover:bg-surface-sunken',
};

const SIZE_CLASSES: Record<ButtonSize, string> = {
  // Heights come from the density scale so a button in a grid toolbar lines up with
  // the rows beside it. No fixed widths anywhere — G6 budgets 40% string expansion
  // and a fixed-width button is the first thing that breaks.
  sm: 'h-6 px-2 text-label gap-1',
  md: 'h-8 px-3 text-body gap-2',
  // ADR 9004 - the page-level action: the sign-in button, and the one call to action on a
  // screen that has exactly one. 48px is the density scale's own step (`space-12`); the
  // mockup draws 52, which is not on the scale and would be the only height in the system
  // that is not. A size rather than a height passed through `className`: both classes
  // would land on the element and the stylesheet's order would decide, which is how the
  // first attempt at this rendered a 32px button while asking for 48.
  lg: 'h-12 px-4 text-subsection gap-2',
};

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant;
  readonly size?: ButtonSize;
  /** Renders as the child element instead of a `<button>` — for a link that looks
   *  like a button, without duplicating the styles. */
  readonly asChild?: boolean;
  readonly loading?: boolean;
  /** Lucide icon element. Decorative: the accessible name comes from the label. */
  readonly icon?: React.ReactNode;
  /**
   * Where the label sits in a button wider than its content.
   *
   * `center` is the default and is right for a toolbar button, which is only as wide as
   * its label. `start` is for a page-level call to action and any `w-full` button: the
   * brand guide sets the label against the leading edge with the icon at the far end, and
   * a centred label in a 360px pill reads as a dialog's OK button rather than as the one
   * thing this screen is for.
   *
   * Under `start` the icon is rendered AFTER the label, because it is then a direction
   * marker at the end of the line rather than a glyph introducing it.
   */
  readonly align?: 'center' | 'start';
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'secondary', size = 'md', asChild = false, loading = false, icon, align = 'center', className, children, disabled, ...rest },
  ref,
) {
  const Component = asChild ? Slot : 'button';
  const glyph = loading
    ? <Loader2 className="animate-spin" size={size === 'sm' ? 12 : 14} aria-hidden />
    : icon;
  return (
    <Component
      ref={ref}
      // G2 §2.6 — no mouse-only affordance; a button is always keyboard-operable and
      // the focus ring is never removed.
      className={[
        // ADR 9004 — the pill. Brand guide p.55; `radius-button` is 20px rather than a
        // half-height rule, so the two sizes agree about what a pill is.
        'inline-flex items-center rounded-button font-ui font-medium',
        // Not appended through `className` by the caller: both `justify-*` classes would
        // land on the element and the STYLESHEET's order would decide which won, which is
        // the same trap the `lg` size note below describes.
        align === 'start' ? 'justify-between' : 'justify-center',
        'transition-colors duration-fast',
        'disabled:cursor-not-allowed disabled:opacity-50',
        // Never `whitespace-nowrap` with a fixed width: the label must be allowed to
        // wrap or the button to grow (G6 layout budget).
        'text-start',
        SIZE_CLASSES[size],
        VARIANT_CLASSES[variant],
        FOCUS_RING,
        className ?? '',
      ].join(' ')}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {align === 'start' ? children : glyph}
      {align === 'start' ? glyph : children}
    </Component>
  );
});

/**
 * An icon-only button. Separate from `Button` because an icon-only control MUST have
 * an accessible name and there is no way to omit it here — the prop is required.
 */
// `align` is omitted as well: an icon-only button has one child and nothing to align, and
// `align="start"` would give it `justify-between` with a single item — no visible change
// today and a silently wrong one the moment a badge is added beside the glyph.
export interface IconButtonProps extends Omit<ButtonProps, 'children' | 'icon' | 'align'> {
  readonly icon: React.ReactNode;
  /** Required. An icon-only button with no accessible name fails axe and is
   *  unusable with a screen reader; the type will not let you ship one. */
  readonly 'aria-label': string;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { icon, size = 'md', className, ...rest }, ref,
) {
  return (
    <Button
      ref={ref}
      size={size}
      // An icon-only button keeps the control radius: a 20px pill on a 32px square is a
      // circle, and the guide's pill is a shape for a LABEL. Width matches height for
      // every size on the scale, not just `sm` vs. everything else.
      // `justify-center` is no longer appended here: `Button` now owns it through `align`,
      // whose default is `center`, and two `justify-*` classes on one element are decided
      // by the stylesheet rather than by this line.
      className={[{ sm: 'w-6 px-0', md: 'w-8 px-0', lg: 'w-12 px-0' }[size], 'rounded-control', className ?? ''].join(' ')}
      {...rest}
    >
      {icon}
    </Button>
  );
});
